// Event rating rules for the console (OTC-23, OTC-692, OTC-104).
// Backend twin: backend/<lambda>/ratings-core.mjs (canonical copy tests/backend/fixtures/ratings-core.mjs).
// Validation and formatting here must match it; tests/ratings.test.js and the backend test share cases.

export type RatingDisplayType = 'icons' | 'labels'

export interface RatingScale {
  id: number
  name: string
  display_type: RatingDisplayType
  max_value: number
  allow_half: boolean
  levels: string[]
  icon: string
  color: string
  position: number
  archived: boolean
  rated_event_count?: number
}

// One event's rating on one scale, as get-game-by-id returns it and the event save sends it.
export interface GameRating {
  rating_scale_id: number
  value: number | null
  label: string | null
  icon: string | null
  color: string | null
  show_to_customers: boolean
}

export interface RatingIconOption {
  name: string
  type: 'bootstrap' | 'custom'
  value: string
}

const BOOTSTRAP_ICONS = [
  'star', 'heart', 'alarm', 'award', 'bag', 'bell', 'diamond', 'dice3', 'dice5', 'droplet', 'egg', 'pencil',
  'emoji-smile', 'emoji-heart-eyes', 'emoji-sunglasses', 'envelope', 'exclamation-triangle', 'eye', 'gift', 'gear', 'hand-thumbs-up',
  'book', 'bookmark', 'bug', 'calculator', 'clock', 'cloud', 'cpu', 'handbag', 'hexagon', 'lightning', 'lock',
  'camera', 'camera-reels', 'chat', 'check-circle', 'circle', 'plug', 'puzzle', 'question-circle', 'shield-lock', 'signpost',
  'suit-club', 'suit-diamond', 'suit-heart', 'suit-spade', 'stopwatch', 'tag', 'telephone', 'tree', 'trophy', 'unlock',
]

// Escape-room icons that Bootstrap does not have; rendered as tim-icons with a -fill variant.
export const CUSTOM_RATING_ICONS = ['icon-dragon', 'icon-hourglass', 'icon-rocket', 'icon-skull', 'icon-witch-hat']

// Most ratings one event can carry. games-api-edit-game and games-api-create-game enforce the same number.
export const MAX_RATINGS_PER_EVENT = 10

export const RATING_ICON_OPTIONS: RatingIconOption[] = [
  ...BOOTSTRAP_ICONS.map(icon => ({ name: icon, type: 'bootstrap' as const, value: icon })),
  ...CUSTOM_RATING_ICONS.map(icon => ({ name: icon.replace(/^icon-/, ''), type: 'custom' as const, value: icon })),
].sort((a, b) => a.name.localeCompare(b.name))

export const MIN_ICONS = 2
export const MAX_ICONS = 10
export const MIN_LEVELS = 2
export const MAX_LEVELS = 10

export function isCustomRatingIcon(icon: string | null | undefined): boolean {
  return !!icon && CUSTOM_RATING_ICONS.includes(icon)
}

export function filledIcon(icon: string): string {
  return icon + '-fill'
}

// "witch-hat" -> "Witch hat"
export function iconLabel(name: string): string {
  const text = name.replace(/-/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function parseLevels(levels: unknown): string[] {
  if (Array.isArray(levels)) return levels as string[]
  if (typeof levels === 'string' && levels !== '') {
    try {
      const parsed = JSON.parse(levels)
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      return []
    }
  }
  return []
}

type ScaleShape = { display_type: RatingDisplayType | string, max_value: number | string, allow_half?: boolean | number, levels?: unknown }

export function ratingMax(scale: ScaleShape): number {
  return scale.display_type === 'labels' ? parseLevels(scale.levels).length : Number(scale.max_value)
}

export function ratingStep(scale: ScaleShape): number {
  return scale.display_type === 'icons' && Number(scale.allow_half) === 1 ? 0.5 : 1
}

// Lowest value a rating can hold: 0 for icons (drawn as all-empty icons), the first level for named levels.
export function ratingMin(scale: ScaleShape): number {
  return scale.display_type === 'labels' ? 1 : 0
}

export function isValidRatingValue(value: unknown, scale: ScaleShape): boolean {
  if (value === null || value === undefined) return true
  const v = Number(value)
  if (!Number.isFinite(v)) return false
  const step = ratingStep(scale)
  if (v < ratingMin(scale) || v > ratingMax(scale)) return false
  return step === 0.5 ? Number.isInteger(v * 2) : Number.isInteger(v)
}

export function formatRatingValue(value: unknown, scale: ScaleShape, numberFormat: number | string = 0): string {
  if (value === null || value === undefined || value === '') return ''
  const v = Number(value)
  if (!Number.isFinite(v)) return ''
  if (scale.display_type === 'labels') return parseLevels(scale.levels)[v - 1] || ''
  let text = Number.isInteger(v) ? String(v) : v.toFixed(1)
  if (Number(numberFormat) === 1) text = text.replace('.', ',')
  return text + '/' + Number(scale.max_value)
}

// Sample value for showing a scale: about 60% filled so the row reads as filled and empty icons,
// ending on a half icon when the scale allows halves.
export function ratingPreviewValue(scale: { max_value: number | string, allow_half?: boolean | number }): number {
  const filled = Math.max(1, Math.ceil(Number(scale.max_value) * 0.6))
  return Number(scale.allow_half) === 1 ? filled - 0.5 : filled
}

// How the n-th icon (1-based) renders for a value: 3.5 gives full for 1..3, half for 4, empty for 5.
export function iconFill(value: number | null | undefined, n: number): 'full' | 'half' | 'empty' {
  const v = Number(value) || 0
  if (v >= n) return 'full'
  if (v >= n - 0.5) return 'half'
  return 'empty'
}
